import {EntityObject, getObjectId, getObjectType, Message, messageType as messageTypes, useCreateMessageMutation} from "@ots-frontend/ots-data";
import {useCallback} from "react";
import {useDisclosure} from "@mantine/hooks";
import {notifications} from "@mantine/notifications";
import {useTranslation} from "react-i18next";
import { useAuth } from "../../../providers/AuthProvider/AuthProvider";

export type UseEntityCommentModalProps = {
  entity: EntityObject
  messageType?: string
}

export type UseEntityCommentModalResult = {
  readonly opened: boolean
  readonly handlers: {
    readonly open: () => void;
    readonly close: () => void;
    readonly toggle: () => void;
  }
  readonly onSubmit: (comments: string | undefined) => void;
  readonly isCreating: boolean
}

export const useEntityCommentModal = (props: UseEntityCommentModalProps): UseEntityCommentModalResult => {
  // "MESSAGE" was never a server code; the server filed it as Comment anyway.
  const {entity, messageType = messageTypes.comment.code} = props;

  const {userId} = useAuth()

  const [_opened, _handlers] = useDisclosure(false);

  const [_createMessage, {isLoading: _isCreating}] = useCreateMessageMutation();

  const {t} = useTranslation()

  const _onSubmit = useCallback((comment: string | undefined) => {
    if (comment) {
      const message: Partial<Message> = {
        objectId: getObjectId(entity),
        objectType: getObjectType(entity),
        messageType: messageType,
        message: comment,
        personId: userId,
      }

      _createMessage(message)
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: t("commons.entity.message.alert.create.title", "Comment Added"),
            message: ""
          });
          _handlers.close()
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: t("commons.error", "Error"),
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    } else {
      _handlers.close()
    }
  }, [_createMessage, entity, messageType, userId, _handlers, t])


  return {
    opened: _opened,
    handlers: _handlers,
    onSubmit: _onSubmit,
    isCreating: _isCreating,
  };
};
